import {t} from "@lingui/core/macro";
import {call, put, select, take, takeLatest, takeLeading} from "redux-saga/effects";

import {LOGIN_FINISHED, RECEIVE_USER} from "@atg-global-shared/user/userActionTypes";
import {AuthActions, AuthSelectors} from "@atg-shared/auth";
import log, {serializeError} from "@atg-shared/log";
import {ModalActions} from "@atg-shared/modals-data-access";
import {ApolloClientInstance} from "@atg-tillsammans/apollo-data-access";
import {
    MyTeamsDocument,
    TeamContextDocument,
} from "@atg-tillsammans/graphql/query/__generated__/teamQueries.graphql.generated";
import {
    TeamMemberActions,
    TeamMemberSelectors,
} from "@atg-tillsammans/team-member-data-access";
import {TeamPageUtils} from "@atg-tillsammans/team-utils";
import {type MemberTeam} from "@atg-tillsammans/types";

import * as TeamActions from "./team.actions";
import * as TeamApi from "./teamApi";

import type {TeamRequestError} from "../../domain/team.types";
import type {SagaIterator} from "redux-saga";

export function* initMyTeams(): SagaIterator {
    yield put(TeamMemberActions.fetchMemberTeams({refreshTeams: true}));
}

/**
 * @param action: CreateTeamAction
 * @returns
 */
export function* createTeam({payload}: TeamActions.CreateTeamAction): SagaIterator {
    yield put(AuthActions.checkAuth(true));

    const authResult = yield take(AuthActions.AUTH_CHECK_RESPONSE);

    if (authResult.error) {
        return;
    }

    try {
        const result = yield call(TeamApi.createTeam, payload);

        if (result?.ok && result?.data) {
            yield put(TeamActions.teamCreated(result.data.id));
        }

        if (!result?.ok && result?.data?.errorCode) {
            const message = TeamPageUtils.getTeamErrorMessage(result.data.errorCode);
            yield put(TeamActions.createTeamError(message));
        }
    } catch (error: unknown) {
        const {response} = error as TeamRequestError;
        const message = TeamPageUtils.getTeamErrorMessage(response?.data?.errorCode);
        yield put(TeamActions.createTeamError(message ?? t`Okänt tekniskt fel`));
        log.error(serializeError(error));
    }
}

/**
 * Sometimes teams have not been added to the BE cache before we try to get them again and the new team
 * will not be available in my teams. To prevent this we check if the team is already fetched or else
 * reloads my teams.
 *
 * @param action: TeamCreatedAction
 * @returns
 */
export function* teamCreated({payload}: TeamActions.TeamCreatedAction): SagaIterator {
    const isLoggedIn = yield select(AuthSelectors.isLoggedIn);

    if (!isLoggedIn) return;

    const teams: MemberTeam[] | null = yield select(TeamMemberSelectors.selectTeams);
    if (!teams || (teams && !teams.find(({id}) => payload.teamId === id))) {
        yield put(
            TeamMemberActions.invalidateAndRefetchMemberTeams({refreshTeams: true}),
        );
    }
}

export function* getTeamMembers(
    action: TeamActions.ReloadTeamMembersAction | TeamActions.SetTeamAction,
): SagaIterator {
    const isLoggedIn = yield select(AuthSelectors.isLoggedIn);

    yield put(TeamActions.getTeamMembers(action.payload.teamId, !isLoggedIn));
}

export function* getTeamMembersFromJoinedTeam({
    payload,
}: TeamActions.MemberJoinedTeamAction): SagaIterator {
    const isLoggedIn = yield select(AuthSelectors.isLoggedIn);
    const teamId = payload.team?.id;

    if (!teamId) return;

    yield put(TeamActions.getTeamMembers(teamId, !isLoggedIn));
}

/**
 * The team image is uploaded via a plain REST call, so Apollo's cache for the `Team`
 * (team page) and `MemberTeam` (my teams list) entities isn't automatically updated.
 * Refetching these active queries keeps the UI in sync without needing to leave/re-enter
 * the page, without us having to know/maintain the cache's internal field shape.
 */
export async function refetchTeamImageQueries() {
    if (!ApolloClientInstance.exists()) return;

    await ApolloClientInstance.getClient().refetchQueries({
        include: [TeamContextDocument, MyTeamsDocument],
    });
}

export function* uploadTeamImage({
    payload,
}: TeamActions.UploadTeamImageAction): SagaIterator {
    if (!payload.teamId) {
        yield put(TeamActions.uploadTeamImageError(t`teamId is missing`));
        return;
    }

    try {
        const response = yield call(TeamApi.saveTeamImage, payload.teamId, payload.file);
        if (response.ok) {
            try {
                yield call(refetchTeamImageQueries);
            } catch (error: unknown) {
                log.error(serializeError(error));
            }
            yield put(TeamActions.updateTeamImage(response.data.imageUrl));
            yield put(ModalActions.closeImageUploadModal());
            yield put(TeamActions.uploadTeamImageSuccess(response.data.imageUrl));
        }
    } catch (error: unknown) {
        yield put(
            TeamActions.uploadTeamImageError((error as Error).message, error as Error),
        );
    }
}

function clearApplication() {
    const apolloClient = ApolloClientInstance.getClient();
    const cache = apolloClient?.cache;
    if (!cache) return;

    cache.evict({
        fieldName: "applications",
    });
    cache.gc();
}

export function* revokeApplication(): SagaIterator {
    yield call(clearApplication);
}

export default function* teamSaga() {
    yield takeLeading([LOGIN_FINISHED, RECEIVE_USER], initMyTeams);
    yield takeLatest(
        [TeamActions.RELOAD_TEAM_MEMBERS, TeamActions.RECEIVE_TEAM],
        getTeamMembers,
    );
    yield takeLatest(TeamActions.TEAM_MEMBER_JOINED, getTeamMembersFromJoinedTeam);
    yield takeLatest(TeamActions.UPLOAD_TEAM_IMAGE, uploadTeamImage);
    yield takeLatest(TeamActions.TEAM_CREATED, teamCreated);
    yield takeLatest(TeamActions.CREATE_TEAM, createTeam);
    yield takeLatest(
        [
            TeamActions.RECEIVE_REVOKE_APPLICATION,
            TeamActions.INVALIDATE_APPLICATION_CACHE,
        ],
        revokeApplication,
    );
}
